import { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Drawer,
    Typography,
    IconButton,
    Button,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { httpClient } from '../../common/httpclient';
import Dropdown from '../../component/dropdown/Dropdown';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';

export default function EditQuoteModal({ open, onClose, quoteId, onSuccess }) {
    const { settings } = useEnterpriseConfigContext();
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [observations, setObservations] = useState('');
    const [defectReason, setDefectReason] = useState('');
    const [quoteData, setQuoteData] = useState(null);

    // Carregar clientes
    useEffect(() => {
        if (open) {
            httpClient.get('/customers')
                .then((response) => {
                    setCustomers(response.data || []);
                })
                .catch((error) => {
                    console.error('Erro ao carregar clientes:', error);
                });
        }
    }, [open]);

    // Carregar dados do orçamento e veículos
    useEffect(() => {
        if (open && quoteId) {
            // Carregar dados do orçamento
            httpClient.get(`/servicequotes/${quoteId}`)
                .then((response) => {
                    const data = response.data;
                    setQuoteData(data);
                    setSelectedCustomer(data.customer_id || '');
                    setSelectedVehicle(data.vehicle_id || '');
                    setObservations(data.observations || '');
                    setDefectReason(data.defect_reason || '');

                    // Carregar veículos do cliente se houver cliente
                    if (data.customer_id) {
                        httpClient.get(`/vehicles/customer/${data.customer_id}`)
                            .then((vehicleResponse) => {
                                setVehicles(vehicleResponse.data || []);
                            })
                            .catch((error) => {
                                console.error('Erro ao carregar veículos:', error);
                                setVehicles([]);
                            });
                    }
                })
                .catch((error) => {
                    console.error('Erro ao carregar dados do orçamento:', error);
                });
        } else {
            // Resetar campos quando fechar
            setSelectedCustomer('');
            setSelectedVehicle('');
            setObservations('');
            setDefectReason('');
            setVehicles([]);
            setQuoteData(null);
        }
    }, [open, quoteId]);

    // Carregar veículos quando cliente mudar
    useEffect(() => {
        if (selectedCustomer) {
            httpClient.get(`/vehicles/customer/${selectedCustomer}`)
                .then((response) => {
                    setVehicles(response.data || []);
                    // Limpar veículo selecionado se não estiver mais na lista
                    const currentVehicleId = selectedVehicle;
                    if (currentVehicleId && !response.data.find(v => (v.ID || v.Id || v.id) === currentVehicleId)) {
                        setSelectedVehicle('');
                    }
                })
                .catch((error) => {
                    console.error('Erro ao carregar veículos:', error);
                    setVehicles([]);
                });
        } else {
            setVehicles([]);
            setSelectedVehicle('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomer]);

    const handleSubmit = async () => {
        if (!selectedCustomer) {
            return;
        }

        setLoading(true);
        try {
            const updateData = {
                customer_id: selectedCustomer,
                vehicle_id: selectedVehicle || null,
                observations: observations || '',
                defect_reason: defectReason || ''
            };

            const response = await httpClient.put(`/servicequotes/${quoteId}`, updateData);
            
            if (response.status === 200 || response.status === 204) {
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            console.error('Erro ao atualizar orçamento:', error);
            throw error; // Re-throw para o DialogForm tratar
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = (value) => {
        setSelectedCustomer(value);
    };

    const handleVehicleChange = (value) => {
        setSelectedVehicle(value);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)'
                    }
                }
            }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 560, md: 640 },
                    maxWidth: '100vw',
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 0,
                    boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            <Box sx={{
                px: 3,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Dados do Orçamento
                </Typography>
                <IconButton onClick={onClose} size="small" disabled={loading}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 0.5 }}>
                    <Box>
                        <Dropdown
                            label="Cliente"
                            labelVariant="static-micro"
                            data={customers.map(customer => ({
                                id: customer.ID || customer.id,
                                name: customer.Name || customer.name
                            }))}
                            selectedItem={selectedCustomer}
                            handleChange={handleCustomerChange}
                            variant="outlined"
                            size="medium"
                            sx={standaloneFieldSx}
                            required
                        />
                    </Box>

                    {settings?.os_use_vehicle && (
                        <Box>
                            <Dropdown
                                label="Veículo"
                                labelVariant="static-micro"
                                data={vehicles.map(vehicle => ({
                                    id: vehicle.ID || vehicle.Id || vehicle.id,
                                    name: `${vehicle.Manufacturer || vehicle.manufacturer || ''} - ${vehicle.Model || vehicle.model || ''}`
                                }))}
                                selectedItem={selectedVehicle}
                                handleChange={handleVehicleChange}
                                variant="outlined"
                                size="medium"
                                sx={standaloneFieldSx}
                                disabled={!selectedCustomer}
                            />
                        </Box>
                    )}

                    <Box>
                        <TextField
                            label="Motivo/Defeito"
                            multiline
                            rows={3}
                            fullWidth
                            value={defectReason}
                            onChange={(e) => setDefectReason(e.target.value)}
                            variant="outlined"
                            size="medium"
                            sx={standaloneFieldSx}
                            placeholder="Digite o motivo da entrada ou o defeito relatado..."
                        />
                    </Box>

                    <Box>
                        <TextField
                            label="Observações"
                            multiline
                            rows={4}
                            fullWidth
                            value={observations}
                            onChange={(e) => setObservations(e.target.value)}
                            variant="outlined"
                            size="medium"
                            sx={standaloneFieldSx}
                            placeholder="Digite as observações do orçamento..."
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                px: 3,
                py: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider'
            }}>
                <Button variant="outlined" onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
                </Button>
            </Box>
        </Drawer>
    );
}

